import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { type AIContext, useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import type { RouteSidebarEffectParams } from './types';

type Params = {
  pathname: string;
  setAiContextMenu: (context: AIContext | undefined) => void;
} & RouteSidebarEffectParams;

/**
 * ルート変更時にサイドバーの状態を管理する
 *
 * @remarks
 * - 通常のルート変更時: サイドバーを閉じる（ダーティ状態の場合は確認ダイアログ表示）
 * - 特定条件（fromPdf=true, threadId指定）: サイドバーを開く
 */
export const useRouteSidebarEffect = ({ pathname, setAiContextMenu, route, params }: Params) => {
  const previousPathRef = useRef(pathname);
  const setRightSidebarOpen = useRouteSidePanelStore((state) => state.setOpen);
  const requestAction = useRouteSidePanelStore((state) => state.requestAction);
  const navigate = useNavigate();

  // 各ルートのパラメータを型安全に抽出
  const fromPdf = route === 'projectCreate' ? params?.fromPdf : undefined;
  const detailThreadId = route === 'projectDetail' ? params?.threadId : undefined;

  useEffect(() => {
    const resetContext = () => {
      if (previousPathRef.current !== pathname) {
        previousPathRef.current = pathname;
        requestAction(() => {
          setAiContextMenu(undefined);
          setRightSidebarOpen({ isOpen: false, mode: undefined });
        });
      }
    };

    switch (route) {
      case 'projectCreate': {
        resetContext();
        if (fromPdf) {
          navigate({ to: '/projects/create', search: { fromPdf: false }, replace: true });
          const context: AIContext = { route: 'projectCreate', mode: 'createFromPdf' };
          setAiContextMenu(context);
          setRightSidebarOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
        }
        break;
      }
      case 'projectDetail': {
        resetContext();
        // ?threadId=xxx 付きで遷移した場合（メモのスレッドリンク経由など）、
        // 該当スレッドを開いた状態の AI チャットパネルを自動表示する。
        if (detailThreadId) {
          // 既に同じ threadId で開いている場合は no-op（再 set による subscriber 通知を避ける）
          const current = useRouteSidePanelStore.getState();
          const alreadyOpenWithThread =
            current.isOpen &&
            current.mode?.type === 'ai-chat' &&
            current.mode.context?.route === 'projectDetail' &&
            current.mode.context.mode === 'agent_chat' &&
            current.mode.context.threadId === detailThreadId;
          if (!alreadyOpenWithThread) {
            const context: AIContext = {
              route: 'projectDetail',
              mode: 'agent_chat',
              threadId: detailThreadId,
            };
            setRightSidebarOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
          }
        }
        break;
      }
      case 'projectEdit': {
        resetContext();
        break;
      }
      default:
        resetContext();
    }
  }, [
    pathname,
    navigate,
    setAiContextMenu,
    setRightSidebarOpen,
    requestAction,
    route,
    fromPdf,
    detailThreadId,
  ]);
};
